import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactLoading from "react-loading";
import { format } from "date-fns";
import { ClosureItem } from "../../../../components/ClosureItem";
import { i18n } from "../../../../localization/i18n";
import { Modal } from "../../../../components/Modal";
import { Bill, useAuth } from "../../../../hooks/AuthContext";
import { currencyValue } from "../../../../components/UsersProfile/mock";
import api from "../../../../services/api";
import { BillPaymentDetails } from "../../../../components/BillPaymentDetails";
import NoInfo from "../../../../components/NoInfo";
import { roles } from "../../../../utils/constants";
import { Container, Content } from "./styles";

export default function UserBills() {
  const { user, currency, setNavigationAction } = useAuth();
  const navigate = useNavigate();

  const [userBills, setUserBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCardSelectModal, setOpenCardSelectModal] = useState<number | null>(null);

  function formatDate(date: Date) {
    if (!date || isNaN(date.getTime())) {
      return "-";
    }
    return format(date, "dd/MM/yyyy");
  }

  function toggleCardSelectModal() {
    setOpenCardSelectModal(null);
  }

  function getDates(monthAndYear: string | undefined | null, isEndDate: boolean): Date {
    if (!monthAndYear || typeof monthAndYear !== "string" || !monthAndYear.includes("/")) {
      return new Date();
    }
    const splitedDate = monthAndYear.split("/").map((date) => Number(date));
    const month = splitedDate[0];
    const year = splitedDate[1];
    if (!month || !year || isNaN(month) || isNaN(year)) {
      return new Date();
    }
    return new Date(year, isEndDate ? month - 1 : month - 2, 26);
  }

  async function getLicenseeBills() {
    setLoading(true);
    try {
      const response = await api.get(`/bills`);
      const rawBills = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      setUserBills(rawBills);
    } catch (error) {
      setUserBills([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getLicenseeBills();
    setNavigationAction({
      hasButton: false,
      description: `${i18n.t("bills.bills")}`,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const closureRoutePrefix =
    user?.role === roles.admin ? "admin" : "licensee";

  return (
    <Container>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
          <ReactLoading
            type={"bubbles"}
            color={"var(--fixit)"}
            height={64}
            width={64}
          />
        </div>
      ) : userBills.length === 0 ? (
        <NoInfo noInfo={`${i18n.t("noInfo.bill")}`} />
      ) : (
        userBills.map((bill, index) => (
          <Content key={bill.id || index}>
            {openCardSelectModal === index && (
              <Modal
                open={openCardSelectModal === index}
                onRequestClose={toggleCardSelectModal}
                modalWidth={500}
              >
                <BillPaymentDetails
                  bill={bill}
                  reloadUser={getLicenseeBills}
                  onHandleCancel={toggleCardSelectModal}
                  totalBillValue={Number(bill.bill_value || 0) - Number(bill.paid_ammount || 0)}
                  closure_date={getDates(bill.referring_period, true) ?? new Date()}
                  currency={currency?.code ?? currencyValue.BRL}
                />
              </Modal>
            )}

            <ClosureItem
              currency={currency?.code}
              bill={bill}
              closurePeriod={getDates(bill.referring_period, true)}
              acceptPayment={
                bill.payment_situation !== "PAYMENT_RECEIVED" &&
                bill.payment_situation !== "PENDING"
              }
              onHandlePayBill={() => setOpenCardSelectModal(index)}
              title={`${i18n.t("userProfile.previousClosure")} (${
                currency?.code ?? currencyValue.BRL
              }) ${bill.referring_period || ""}`}
              description={`${i18n.t("userProfile.descriptions.closure")}`}
              period={`${formatDate(
                getDates(bill.referring_period, false)
              )} - ${formatDate(getDates(bill.referring_period, true))}`}
              help={`${i18n.t("userProfile.descriptions.closurePopover")}`}
              value={Number(bill.bill_value || 0).toFixed(2)}
              onClick={() => {
                navigate({
                  pathname: `/${closureRoutePrefix}/orders/closure`,
                  search: `?from=${formatDate(
                    getDates(bill.referring_period, false)
                  )}&to=${formatDate(getDates(bill.referring_period, true))}`,
                });
              }}
            />
          </Content>
        ))
      )}
    </Container>
  );
}

